import axiosInstance from '@/configs/axios';
import type { CampaignPriceChangeReason } from '@/interface/campaign';

import { clickIdBody } from './click-id';

export interface CampaignDiscountPreviewArgs {
  plan_id: number;
  gateway_id: number;
  payment_method?: 'pix' | 'boleto' | null;
  country_code?: string;
  squad_type_id?: number | null;
}

interface PreviewResponse {
  applies: boolean;
  reason?: string;
  percentage?: string;
}

/**
 * The API's refusal vocabulary mapped onto this site's. Keyed by plain string, not by a union of the
 * server's values, on purpose — see `mapRemoteReason`.
 *
 * ⚠️ `gateway_not_eligible` from the API is ALWAYS about the single `gateway_id` in the request, so it
 * maps to `gateway_not_eligible_selected`. Mapping it onto the local key of the same name — which is
 * the aggregate verdict over every method of the plan — would tell the visitor "no payment method
 * carries this offer" when the truth is "not this one; switch and the discount comes back". A worse
 * message, and a false one.
 *
 * The three gateway-side refusals collapse into that same key because its copy is already true for
 * all of them, and because nothing the visitor could do about them differs: a coupon still syncing
 * with the gateway and a gateway that cannot carry it both mean "pay another way".
 */
const REMOTE_REASONS: Record<string, CampaignPriceChangeReason> = {
  country_not_eligible: 'country_not_eligible',
  per_customer_limit: 'per_customer_limit',
  recurring_unsupported_gateway: 'recurring_unsupported_gateway',
  gateway_not_eligible: 'gateway_not_eligible_selected',
  gateway_sync_pending: 'gateway_not_eligible_selected',
  gateway_unsynced: 'gateway_not_eligible_selected',
  not_supported_on_gateway: 'gateway_not_eligible_selected',
};

/**
 * A refusal reason from the server, as one of this site's reasons.
 *
 * `CampaignPriceChangeReason` is deliberately NOT exhaustive over the server's vocabulary: a coupon
 * that dies between the storefront and the checkout answers `disabled`, `expired`, `exhausted`,
 * `not_found` or `plan_not_eligible`, and the resolver has an `?? 'invalid'` branch on top of that.
 * Anything unrecognized becomes the generic, so a raw server string can never reach
 * CAMPAIGN_PRICE_CHANGE_KEYS and render as a missing translation.
 */
export function mapRemoteReason(reason: string | undefined): CampaignPriceChangeReason {
  return (reason && REMOTE_REASONS[reason]) || 'unavailable';
}

/**
 * What one pre-check answered. `null` used to do three jobs at once, and since the answer arms a
 * payment form they have to be told apart:
 *
 * - `settled` + `reason: null` — the server has no objection. Also `applies:false` with no reason,
 *   which means there was no discount to preview (toggle off, no `click_id`, no campaign coupon for
 *   this plan, a typed coupon): turning that into the generic reason would make every page with the
 *   toggle off announce a price change.
 * - `settled` + a reason — the discount will not reach the charge, and why.
 * - `failed` — 401/422/429/5xx/network. The caller reports it as `'unknown'`, never as "no
 *   objection".
 * - `aborted` — the visitor moved on; the caller drops it, which is what makes last-response-wins
 *   hold.
 */
export type CampaignDiscountPreviewResult = { status: 'settled'; reason: CampaignPriceChangeReason | null } | { status: 'failed'; httpStatus?: number } | { status: 'aborted' };

// A failure RELEASES the payment form, so asking again before giving up is the cheapest way to keep
// that release rare — and the likeliest failure is a 429 from the coupon field's shared 30/min
// bucket, which a few seconds of waiting outlasts. Two retries is where it stops: the whole budget
// already sits close to the gate's own 5s watchdog, so a third would land after the form was
// released anyway.
const PREVIEW_RETRY_DELAY_MS = 1200;
const PREVIEW_SECOND_RETRY_DELAY_MS = 3000;

function isAbortError(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name;
  return name === 'CanceledError' || name === 'AbortError';
}

// Only a failure the API might answer differently. 401 (no checkout session), 403 and 422 are
// answers about the request itself, and asking twice more cannot change them — it would only spend
// the 30/min bucket this endpoint shares with the coupon field.
function isWorthRetrying(result: CampaignDiscountPreviewResult): boolean {
  if (result.status !== 'failed') {
    return false;
  }

  if (result.httpStatus === undefined) {
    return true;
  }

  return result.httpStatus === 429 || result.httpStatus >= 500;
}

// Resolves on the deadline OR on abort, so no timer outlives the request it belongs to. The early
// return is not redundant: a signal that aborted before the listener was attached never fires it,
// and the wait would run its full delay on a selection nobody is looking at any more.
function waitBeforeRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const timer = setTimeout(resolve, delayMs);
    const stopWaiting = () => {
      clearTimeout(timer);
      resolve();
    };

    // `once` takes the listener off when it fires; if the timer wins instead, it goes with the
    // controller the caller's cleanup already discards.
    signal?.addEventListener('abort', stopWaiting, { once: true });
  });
}

async function askPreviewOnce(args: CampaignDiscountPreviewArgs, signal?: AbortSignal): Promise<CampaignDiscountPreviewResult> {
  try {
    // Same field names as POST /v1/checkout, so this is the payload the charge would carry. click_id
    // is spread first, as everywhere else, and is simply absent when there is no cookie.
    const response = await axiosInstance.post<PreviewResponse>('/campaigns/discount-preview', { ...clickIdBody(), ...args }, { signal });

    if (response.data?.applies !== false || !response.data?.reason) {
      return { status: 'settled', reason: null };
    }

    return { status: 'settled', reason: mapRemoteReason(response.data.reason) };
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) {
      return { status: 'aborted' };
    }

    return { status: 'failed', httpStatus: (error as { response?: { status?: number } } | null)?.response?.status };
  }
}

async function askAfterWaiting(delayMs: number, args: CampaignDiscountPreviewArgs, signal?: AbortSignal): Promise<CampaignDiscountPreviewResult> {
  await waitBeforeRetry(delayMs, signal);

  if (signal?.aborted) {
    return { status: 'aborted' };
  }

  return askPreviewOnce(args, signal);
}

/**
 * Whether the partner discount already on screen will survive to the charge, asked of the API for one
 * plan, gateway and payment method (SCRUM-1760 §1). Never throws — every failure comes back as a
 * value, tagged, so a 429 is not mistaken for "the server has no objection".
 *
 * The caller must gate on the visitor being logged in and on there being no coupon of their own — the
 * API answers 401 for the first and `applies:false` for the second, and both share the coupon field's
 * rate-limit buckets.
 */
export async function previewCampaignDiscount(args: CampaignDiscountPreviewArgs, signal?: AbortSignal): Promise<CampaignDiscountPreviewResult> {
  const first = await askPreviewOnce(args, signal);

  if (!isWorthRetrying(first)) {
    return first;
  }

  const second = await askAfterWaiting(PREVIEW_RETRY_DELAY_MS, args, signal);

  if (!isWorthRetrying(second)) {
    return second;
  }

  return askAfterWaiting(PREVIEW_SECOND_RETRY_DELAY_MS, args, signal);
}
